import { Button, Card } from '@strategy4kids/ui';
import { useEffect, useState } from 'react';
import { api, MARKETPLACE_URL } from '../services/api.js';
import { useCardPackStore } from '../state/card-pack-store.js';
import { useCommunityPackStore } from '../state/community-pack-store.js';
import { ParentSupport, type SupportPrefill } from './ParentSupport.js';

/**
 * The grown-up's list of community card packs on this device: remove one, or report one to support. Shown only to a signed-in parent
 * (it sits in the parent dashboard) and only when the marketplace is configured and the device has such a pack. Reporting uses the parent's
 * support ticket (`ParentSupport`), so a report always comes from an accountable adult and no child's words go anywhere.
 */
export function CommunityPacksPanel({ token }: { readonly token: string }) {
  const packs = useCommunityPackStore((s) => s.packs);
  const remove = useCommunityPackStore((s) => s.remove);
  const activeId = useCardPackStore((s) => s.packId);
  const setPack = useCardPackStore((s) => s.setPack);
  const [supportOn, setSupportOn] = useState(false);
  const [reporting, setReporting] = useState<SupportPrefill | null>(null);

  useEffect(() => {
    let live = true;
    api
      .supportStatus(token)
      .then((s) => live && setSupportOn(s.configured === true))
      .catch(() => live && setSupportOn(false));
    return () => {
      live = false;
    };
  }, [token]);

  if (MARKETPLACE_URL === '' || packs.length === 0) return null;
  return (
    <Card title="Card packs added to this device" className="flex flex-col gap-3 p-5 sm:p-7" data-testid="community-packs-panel">
      <ul className="flex flex-col gap-3">
        {packs.map((p) => (
          <li key={p.id} className="flex flex-wrap items-center justify-between gap-2" data-testid={`community-pack-row-${p.id}`}>
            <span className="text-base font-bold text-ink">
              {p.name}
              {p.author ? <span className="font-normal text-fg-muted"> by {p.author}</span> : null}
            </span>
            <span className="flex gap-2">
              <Button
                variant="secondary"
                data-testid={`community-pack-remove-${p.id}`}
                onClick={() => {
                  if (activeId === p.id) setPack('classic');
                  remove(p.id);
                }}
              >
                Remove
              </Button>
              {supportOn ? (
                <Button
                  variant="ghost"
                  data-testid={`community-pack-report-${p.id}`}
                  onClick={() =>
                    setReporting({
                      subject: `Report a card pack: ${p.name}`,
                      description: `Pack: ${p.name} (id ${p.id})${p.author ? `\nMade by: ${p.author}` : ''}\n\nWhat is wrong with it?\n`,
                    })
                  }
                >
                  Report
                </Button>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {supportOn ? null : (
        <p className="text-base text-fg-muted" data-testid="community-packs-no-support">
          Reporting is not available yet. You can still remove a pack.
        </p>
      )}
      <ParentSupport token={token} report={reporting} onClose={() => setReporting(null)} />
    </Card>
  );
}
